/**
 * CollectionItemsView - View and manage collection items
 *
 * Displays items in a table format with:
 * - Add new item
 * - Edit existing items
 * - Delete items
 * - Bulk import
 */

import { useState, useEffect, useCallback, useRef } from 'react';
import type { CollectionSchema, FieldDefinition } from '@deep-agents/shared-types';
import { useCollections, type CollectionSchemaRecord, type CollectionItemRecord } from '../../hooks/useCollections';
import { useMediaUpload, formatFileSize } from '../../hooks/useMediaUpload';

// ============================================================
// ICONS
// ============================================================

const ArrowLeftIcon = () => (
  <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 19l-7-7m0 0l7-7m-7 7h18" />
  </svg>
);

const PlusIcon = () => (
  <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
  </svg>
);

const EditIcon = () => (
  <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
  </svg>
);

const TrashIcon = () => (
  <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
  </svg>
);

const UploadIcon = () => (
  <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-8l-4-4m0 0L8 8m4-4v12" />
  </svg>
);

const PhotoIcon = () => (
  <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
  </svg>
);

const VideoIcon = () => (
  <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 10l4.553-2.276A1 1 0 0121 8.618v6.764a1 1 0 01-1.447.894L15 14M5 18h8a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v8a2 2 0 002 2z" />
  </svg>
);

const XIcon = () => (
  <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
  </svg>
);

const SettingsIcon = () => (
  <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" />
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
  </svg>
);

const SparklesIcon = () => (
  <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 3v4M3 5h4M6 17v4m-2-2h4m5-16l2.286 6.857L21 12l-5.714 2.143L13 21l-2.286-6.857L5 12l5.714-2.143L13 3z" />
  </svg>
);

// ============================================================
// TYPES
// ============================================================

interface CollectionItemsViewProps {
  appId: string;
  collection: CollectionSchemaRecord;
  onBack: () => void;
  onEditSchema: () => void;
}

// ============================================================
// COMPONENT
// ============================================================

export function CollectionItemsView({
  appId,
  collection,
  onBack,
  onEditSchema,
}: CollectionItemsViewProps) {
  const {
    items,
    itemsCount,
    isLoadingItems,
    itemsError,
    fetchItems,
    createItem,
    updateItem,
    deleteItem,
    importItems,
    isGenerating,
    generateItems,
  } = useCollections({ appId });

  // UI State
  const [showItemModal, setShowItemModal] = useState(false);
  const [editingItem, setEditingItem] = useState<CollectionItemRecord | null>(null);
  const [showImportModal, setShowImportModal] = useState(false);
  const [showGenerateModal, setShowGenerateModal] = useState(false);
  const [page, setPage] = useState(1);

  const schema = collection.schema;
  const listFields = schema.fields.filter(f => f.showInList);
  const displayFields = listFields.length > 0 ? listFields : schema.fields.slice(0, 4);

  // Load items on mount and when collection changes
  useEffect(() => {
    fetchItems(collection.name, page);
  }, [fetchItems, collection.name, page]);

  // ─────────────────────────────────────────────────────────
  // Handlers
  // ─────────────────────────────────────────────────────────

  const handleAddItem = () => {
    setEditingItem(null);
    setShowItemModal(true);
  };

  const handleEditItem = (item: CollectionItemRecord) => {
    setEditingItem(item);
    setShowItemModal(true);
  };

  const handleDeleteItem = async (item: CollectionItemRecord) => {
    const titleField = schema.displayConfig?.titleField || 'id';
    const title = item.data[titleField] || item.id;

    if (!confirm(`Delete "${title}"?`)) {
      return;
    }

    await deleteItem(collection.name, item.id);
  };

  const handleSaveItem = async (data: Record<string, unknown>) => {
    if (editingItem) {
      await updateItem(collection.name, editingItem.id, data);
    } else {
      await createItem(collection.name, data);
    }
    setShowItemModal(false);
    setEditingItem(null);
  };

  const handleImport = async (items: Record<string, unknown>[]) => {
    const result = await importItems(collection.name, items);
    if (result) {
      setShowImportModal(false);
    }
    return result;
  };

  const handleGenerate = async (prompt: string, count: number) => {
    const result = await generateItems(collection.name, prompt, count);
    if (result) {
      setShowGenerateModal(false);
    }
    return result;
  };

  // ─────────────────────────────────────────────────────────
  // Render
  // ─────────────────────────────────────────────────────────

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="flex items-center gap-3">
        <button
          onClick={onBack}
          className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg transition-colors"
        >
          <ArrowLeftIcon />
        </button>
        <div className="flex-1">
          <h3 className="text-lg font-semibold text-gray-900">
            {schema.label}
          </h3>
          <p className="text-xs text-gray-500">
            {itemsCount} item{itemsCount !== 1 ? 's' : ''}
          </p>
        </div>

        {/* Actions */}
        <button
          onClick={onEditSchema}
          className="flex items-center gap-1.5 px-3 py-1.5 text-sm text-gray-600 hover:text-gray-900 hover:bg-gray-100 rounded-lg transition-colors"
          title="Edit schema"
        >
          <SettingsIcon />
          Schema
        </button>
        <button
          onClick={() => setShowImportModal(true)}
          className="flex items-center gap-1.5 px-3 py-1.5 text-sm text-gray-600 hover:text-gray-900 hover:bg-gray-100 rounded-lg transition-colors"
        >
          <UploadIcon />
          Import
        </button>
        <button
          onClick={() => setShowGenerateModal(true)}
          disabled={isGenerating}
          className="flex items-center gap-1.5 px-3 py-1.5 text-sm text-violet-600 hover:text-violet-700 hover:bg-violet-50 rounded-lg transition-colors disabled:opacity-50"
        >
          <SparklesIcon />
          {isGenerating ? 'Generating...' : 'AI Generate'}
        </button>
        <button
          onClick={handleAddItem}
          className="flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium text-white bg-violet-600 rounded-lg hover:bg-violet-700 transition-colors"
        >
          <PlusIcon />
          Add Item
        </button>
      </div>

      {/* Error */}
      {itemsError && (
        <div className="p-3 bg-red-50 border border-red-200 rounded-lg text-sm text-red-700">
          {itemsError}
        </div>
      )}

      {/* Loading */}
      {isLoadingItems && (
        <div className="flex items-center justify-center py-8">
          <div className="w-6 h-6 border-2 border-violet-600 border-t-transparent rounded-full animate-spin" />
        </div>
      )}

      {/* Empty State */}
      {!isLoadingItems && items.length === 0 && (
        <div className="text-center py-12 bg-gray-50 rounded-lg border-2 border-dashed border-gray-200">
          <p className="text-gray-500 text-sm">No items yet</p>
          <p className="text-gray-400 text-xs mt-1">
            Add items manually or import from a file
          </p>
          <div className="flex items-center justify-center gap-3 mt-4">
            <button
              onClick={handleAddItem}
              className="px-4 py-2 text-sm font-medium text-white bg-violet-600 rounded-lg hover:bg-violet-700 transition-colors"
            >
              Add First Item
            </button>
            <button
              onClick={() => setShowImportModal(true)}
              className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
            >
              Import from File
            </button>
          </div>
        </div>
      )}

      {/* Items Table */}
      {!isLoadingItems && items.length > 0 && (
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-gray-200">
                {displayFields.map(field => (
                  <th
                    key={field.name}
                    className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wide"
                  >
                    {field.label}
                  </th>
                ))}
                <th className="px-3 py-2 text-right text-xs font-medium text-gray-500 uppercase tracking-wide">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {items.map(item => (
                <tr key={item.id} className="hover:bg-gray-50">
                  {displayFields.map(field => (
                    <td key={field.name} className="px-3 py-2 text-gray-900">
                      <FieldValue field={field} value={item.data[field.name]} />
                    </td>
                  ))}
                  <td className="px-3 py-2 text-right">
                    <div className="flex items-center justify-end gap-1">
                      <button
                        onClick={() => handleEditItem(item)}
                        className="p-1.5 text-gray-400 hover:text-gray-600 hover:bg-gray-200 rounded transition-colors"
                        title="Edit"
                      >
                        <EditIcon />
                      </button>
                      <button
                        onClick={() => handleDeleteItem(item)}
                        className="p-1.5 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded transition-colors"
                        title="Delete"
                      >
                        <TrashIcon />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Pagination */}
      {itemsCount > 50 && (
        <div className="flex items-center justify-between pt-4 border-t border-gray-200">
          <p className="text-sm text-gray-500">
            Showing {(page - 1) * 50 + 1} - {Math.min(page * 50, itemsCount)} of {itemsCount}
          </p>
          <div className="flex items-center gap-2">
            <button
              onClick={() => setPage(p => Math.max(1, p - 1))}
              disabled={page === 1}
              className="px-3 py-1.5 text-sm text-gray-600 bg-white border border-gray-300 rounded hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Previous
            </button>
            <button
              onClick={() => setPage(p => p + 1)}
              disabled={page * 50 >= itemsCount}
              className="px-3 py-1.5 text-sm text-gray-600 bg-white border border-gray-300 rounded hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Next
            </button>
          </div>
        </div>
      )}

      {/* Item Edit Modal */}
      {showItemModal && (
        <ItemEditModal
          schema={schema}
          item={editingItem}
          onSave={handleSaveItem}
          onClose={() => {
            setShowItemModal(false);
            setEditingItem(null);
          }}
        />
      )}

      {/* Import Modal */}
      {showImportModal && (
        <ImportModal
          schema={schema}
          onImport={handleImport}
          onClose={() => setShowImportModal(false)}
        />
      )}

      {/* AI Generate Modal */}
      {showGenerateModal && (
        <GenerateModal
          schema={schema}
          isGenerating={isGenerating}
          onGenerate={handleGenerate}
          onClose={() => setShowGenerateModal(false)}
        />
      )}
    </div>
  );
}

// ============================================================
// FIELD VALUE DISPLAY
// ============================================================

function FieldValue({ field, value }: { field: FieldDefinition; value: unknown }) {
  if (value === null || value === undefined) {
    return <span className="text-gray-400">—</span>;
  }

  switch (field.type) {
    case 'boolean':
      return <span>{value ? 'Yes' : 'No'}</span>;

    case 'array':
      if (Array.isArray(value)) {
        return (
          <div className="flex flex-wrap gap-1">
            {value.slice(0, 3).map((v, i) => (
              <span key={i} className="px-1.5 py-0.5 text-xs bg-gray-100 rounded">
                {String(v)}
              </span>
            ))}
            {value.length > 3 && (
              <span className="text-xs text-gray-400">+{value.length - 3}</span>
            )}
          </div>
        );
      }
      return <span>{String(value)}</span>;

    case 'image':
      return value ? (
        <img src={String(value)} alt="" className="w-8 h-8 object-cover rounded" />
      ) : null;

    case 'url':
    case 'video':
      return value ? (
        <a
          href={String(value)}
          target="_blank"
          rel="noopener noreferrer"
          className="text-violet-600 hover:underline truncate block max-w-[200px]"
        >
          {String(value)}
        </a>
      ) : null;

    default:
      return <span className="truncate block max-w-[200px]">{String(value)}</span>;
  }
}

// ============================================================
// ITEM EDIT MODAL
// ============================================================

interface ItemEditModalProps {
  schema: CollectionSchema;
  item: CollectionItemRecord | null;
  onSave: (data: Record<string, unknown>) => Promise<void>;
  onClose: () => void;
}

function ItemEditModal({ schema, item, onSave, onClose }: ItemEditModalProps) {
  const [formData, setFormData] = useState<Record<string, unknown>>(item?.data || {});
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleFieldChange = (fieldName: string, value: unknown) => {
    setFormData(prev => ({ ...prev, [fieldName]: value }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);

    // Validate required fields
    for (const field of schema.fields) {
      if (field.required && !formData[field.name]) {
        setError(`${field.label} is required`);
        return;
      }
    }

    setIsSaving(true);
    try {
      await onSave(formData);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to save');
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
      <div className="bg-white rounded-xl shadow-2xl w-full max-w-lg max-h-[90vh] overflow-hidden flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200">
          <h3 className="text-lg font-semibold text-gray-900">
            {item ? 'Edit Item' : 'New Item'}
          </h3>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600 text-xl">
            ×
          </button>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="flex-1 overflow-y-auto px-6 py-4 space-y-4">
          {error && (
            <div className="p-3 bg-red-50 border border-red-200 rounded-lg text-sm text-red-700">
              {error}
            </div>
          )}

          {schema.fields.map(field => (
            <FieldInput
              key={field.name}
              field={field}
              value={formData[field.name]}
              onChange={(value) => handleFieldChange(field.name, value)}
            />
          ))}
        </form>

        {/* Footer */}
        <div className="flex items-center justify-end gap-3 px-6 py-4 border-t border-gray-200 bg-gray-50">
          <button
            type="button"
            onClick={onClose}
            className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50"
          >
            Cancel
          </button>
          <button
            onClick={handleSubmit}
            disabled={isSaving}
            className="px-4 py-2 text-sm font-medium text-white bg-violet-600 rounded-lg hover:bg-violet-700 disabled:opacity-50"
          >
            {isSaving ? 'Saving...' : (item ? 'Update' : 'Create')}
          </button>
        </div>
      </div>
    </div>
  );
}

// ============================================================
// FIELD INPUT
// ============================================================

interface FieldInputProps {
  field: FieldDefinition;
  value: unknown;
  onChange: (value: unknown) => void;
}

function FieldInput({ field, value, onChange }: FieldInputProps) {
  const inputClass = "w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-violet-500";

  return (
    <div>
      <label className="block text-sm font-medium text-gray-700 mb-1">
        {field.label}
        {field.required && <span className="text-red-500 ml-0.5">*</span>}
      </label>

      {field.type === 'string' && (
        <input
          type="text"
          value={String(value || '')}
          onChange={(e) => onChange(e.target.value)}
          className={inputClass}
        />
      )}

      {field.type === 'number' && (
        <input
          type="number"
          value={value !== undefined && value !== null ? Number(value) : ''}
          onChange={(e) => onChange(e.target.value ? Number(e.target.value) : null)}
          className={inputClass}
        />
      )}

      {field.type === 'boolean' && (
        <button
          type="button"
          onClick={() => onChange(!value)}
          className={`w-14 h-8 rounded-full transition-colors relative ${value ? 'bg-violet-600' : 'bg-gray-300'}`}
        >
          <span className={`absolute top-1 w-6 h-6 bg-white rounded-full shadow transition-transform ${value ? 'translate-x-7' : 'translate-x-1'}`} />
        </button>
      )}

      {field.type === 'enum' && (
        <select
          value={String(value || '')}
          onChange={(e) => onChange(e.target.value)}
          className={inputClass}
        >
          <option value="">-- Select --</option>
          {field.enumValues?.map(opt => (
            <option key={opt} value={opt}>{opt}</option>
          ))}
        </select>
      )}

      {field.type === 'array' && (
        <input
          type="text"
          value={Array.isArray(value) ? value.join(', ') : ''}
          onChange={(e) => onChange(e.target.value.split(',').map(v => v.trim()).filter(Boolean))}
          placeholder="value1, value2, value3"
          className={inputClass}
        />
      )}

      {field.type === 'url' && (
        <input
          type="url"
          value={String(value || '')}
          onChange={(e) => onChange(e.target.value)}
          placeholder="https://..."
          className={inputClass}
        />
      )}

      {(field.type === 'image' || field.type === 'video') && (
        <MediaUploadField
          type={field.type}
          value={String(value || '')}
          onChange={onChange}
        />
      )}

      {field.type === 'date' && (
        <input
          type="date"
          value={String(value || '')}
          onChange={(e) => onChange(e.target.value)}
          className={inputClass}
        />
      )}

      {(field.type === 'reference' || field.type === 'references') && (
        <input
          type="text"
          value={Array.isArray(value) ? value.join(', ') : String(value || '')}
          onChange={(e) => {
            if (field.type === 'references') {
              onChange(e.target.value.split(',').map(v => v.trim()).filter(Boolean));
            } else {
              onChange(e.target.value);
            }
          }}
          placeholder={field.type === 'references' ? 'id1, id2, id3' : 'item_id'}
          className={inputClass}
        />
      )}

      {field.description && (
        <p className="text-xs text-gray-400 mt-1">{field.description}</p>
      )}
    </div>
  );
}

// ============================================================
// IMPORT MODAL
// ============================================================

interface ImportModalProps {
  schema: CollectionSchema;
  onImport: (items: Record<string, unknown>[]) => Promise<{ imported: number } | null>;
  onClose: () => void;
}

function ImportModal({ schema, onImport, onClose }: ImportModalProps) {
  const [jsonText, setJsonText] = useState('');
  const [isImporting, setIsImporting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [result, setResult] = useState<{ imported: number } | null>(null);

  const handleImport = async () => {
    setError(null);

    try {
      const items = JSON.parse(jsonText);
      if (!Array.isArray(items)) {
        throw new Error('JSON must be an array of items');
      }

      setIsImporting(true);
      const result = await onImport(items);
      if (result) {
        setResult(result);
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Invalid JSON');
    } finally {
      setIsImporting(false);
    }
  };

  // Generate example JSON
  const exampleItem: Record<string, unknown> = {};
  for (const field of schema.fields.slice(0, 3)) {
    switch (field.type) {
      case 'string': exampleItem[field.name] = 'Example text'; break;
      case 'number': exampleItem[field.name] = 42; break;
      case 'boolean': exampleItem[field.name] = true; break;
      case 'enum': exampleItem[field.name] = field.enumValues?.[0] || 'option'; break;
      case 'array': exampleItem[field.name] = ['item1', 'item2']; break;
      default: exampleItem[field.name] = 'value';
    }
  }
  const exampleJson = JSON.stringify([exampleItem], null, 2);

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
      <div className="bg-white rounded-xl shadow-2xl w-full max-w-lg max-h-[90vh] overflow-hidden flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200">
          <h3 className="text-lg font-semibold text-gray-900">Import Items</h3>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600 text-xl">
            ×
          </button>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto px-6 py-4 space-y-4">
          {result ? (
            <div className="text-center py-8">
              <div className="w-12 h-12 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <svg className="w-6 h-6 text-green-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                </svg>
              </div>
              <p className="text-lg font-medium text-gray-900">
                {result.imported} items imported
              </p>
              <button
                onClick={onClose}
                className="mt-4 px-4 py-2 text-sm font-medium text-white bg-violet-600 rounded-lg hover:bg-violet-700"
              >
                Done
              </button>
            </div>
          ) : (
            <>
              {error && (
                <div className="p-3 bg-red-50 border border-red-200 rounded-lg text-sm text-red-700">
                  {error}
                </div>
              )}

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  JSON Data
                </label>
                <textarea
                  value={jsonText}
                  onChange={(e) => setJsonText(e.target.value)}
                  placeholder={`Paste JSON array here...\n\nExample:\n${exampleJson}`}
                  rows={12}
                  className="w-full px-3 py-2 text-sm font-mono border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-violet-500 resize-none"
                />
              </div>

              <div className="p-3 bg-gray-50 rounded-lg">
                <p className="text-xs font-medium text-gray-500 mb-2">Expected fields:</p>
                <div className="flex flex-wrap gap-1">
                  {schema.fields.map(f => (
                    <span key={f.name} className="px-1.5 py-0.5 text-xs bg-gray-200 rounded">
                      {f.name}
                      {f.required && <span className="text-red-500">*</span>}
                    </span>
                  ))}
                </div>
              </div>
            </>
          )}
        </div>

        {/* Footer */}
        {!result && (
          <div className="flex items-center justify-end gap-3 px-6 py-4 border-t border-gray-200 bg-gray-50">
            <button
              onClick={onClose}
              className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50"
            >
              Cancel
            </button>
            <button
              onClick={handleImport}
              disabled={isImporting || !jsonText.trim()}
              className="px-4 py-2 text-sm font-medium text-white bg-violet-600 rounded-lg hover:bg-violet-700 disabled:opacity-50"
            >
              {isImporting ? 'Importing...' : 'Import'}
            </button>
          </div>
        )}
      </div>
    </div>
  );
}

// ============================================================
// AI GENERATE MODAL
// ============================================================

interface GenerateModalProps {
  schema: CollectionSchema;
  isGenerating: boolean;
  onGenerate: (prompt: string, count: number) => Promise<{ generated: number } | null>;
  onClose: () => void;
}

function GenerateModal({ schema, isGenerating, onGenerate, onClose }: GenerateModalProps) {
  const [prompt, setPrompt] = useState('');
  const [count, setCount] = useState(5);
  const [error, setError] = useState<string | null>(null);
  const [result, setResult] = useState<{ generated: number } | null>(null);

  const handleGenerate = async () => {
    if (!prompt.trim()) {
      setError('Please enter a prompt');
      return;
    }

    setError(null);
    const res = await onGenerate(prompt, count);
    if (res) {
      setResult(res);
    }
  };

  // Build prompt suggestions based on schema
  const suggestions = [
    `Generate ${count} sample ${schema.label.toLowerCase()} items`,
    `Create ${count} popular ${schema.label.toLowerCase()}`,
    `Add ${count} beginner-friendly ${schema.label.toLowerCase()}`,
  ];

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
      <div className="bg-white rounded-xl shadow-2xl w-full max-w-lg max-h-[90vh] overflow-hidden flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200">
          <div className="flex items-center gap-2">
            <SparklesIcon />
            <h3 className="text-lg font-semibold text-gray-900">AI Generate Items</h3>
          </div>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600 text-xl">
            ×
          </button>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto px-6 py-4 space-y-4">
          {result ? (
            <div className="text-center py-8">
              <div className="w-12 h-12 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <svg className="w-6 h-6 text-green-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                </svg>
              </div>
              <p className="text-lg font-medium text-gray-900">
                {result.generated} items generated!
              </p>
              <button
                onClick={onClose}
                className="mt-4 px-4 py-2 text-sm font-medium text-white bg-violet-600 rounded-lg hover:bg-violet-700"
              >
                Done
              </button>
            </div>
          ) : (
            <>
              {error && (
                <div className="p-3 bg-red-50 border border-red-200 rounded-lg text-sm text-red-700">
                  {error}
                </div>
              )}

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  What would you like to generate?
                </label>
                <textarea
                  value={prompt}
                  onChange={(e) => setPrompt(e.target.value)}
                  placeholder={`Describe what ${schema.label.toLowerCase()} you want to create...`}
                  rows={4}
                  className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-violet-500 resize-none"
                />
              </div>

              {/* Quick suggestions */}
              <div>
                <p className="text-xs font-medium text-gray-500 mb-2">Quick suggestions:</p>
                <div className="flex flex-wrap gap-2">
                  {suggestions.map((suggestion, i) => (
                    <button
                      key={i}
                      onClick={() => setPrompt(suggestion)}
                      className="px-2 py-1 text-xs text-violet-600 bg-violet-50 rounded hover:bg-violet-100 transition-colors"
                    >
                      {suggestion}
                    </button>
                  ))}
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Number of items
                </label>
                <select
                  value={count}
                  onChange={(e) => setCount(Number(e.target.value))}
                  className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-violet-500"
                >
                  {[3, 5, 10, 15, 20].map(n => (
                    <option key={n} value={n}>{n} items</option>
                  ))}
                </select>
              </div>

              <div className="p-3 bg-violet-50 rounded-lg">
                <p className="text-xs text-violet-700">
                  AI will generate {count} {schema.label.toLowerCase()} based on your prompt,
                  automatically filling in all fields according to your schema.
                </p>
              </div>
            </>
          )}
        </div>

        {/* Footer */}
        {!result && (
          <div className="flex items-center justify-end gap-3 px-6 py-4 border-t border-gray-200 bg-gray-50">
            <button
              onClick={onClose}
              className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50"
            >
              Cancel
            </button>
            <button
              onClick={handleGenerate}
              disabled={isGenerating || !prompt.trim()}
              className="flex items-center gap-2 px-4 py-2 text-sm font-medium text-white bg-violet-600 rounded-lg hover:bg-violet-700 disabled:opacity-50"
            >
              {isGenerating ? (
                <>
                  <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                  Generating...
                </>
              ) : (
                <>
                  <SparklesIcon />
                  Generate
                </>
              )}
            </button>
          </div>
        )}
      </div>
    </div>
  );
}

// ============================================================
// MEDIA UPLOAD FIELD
// ============================================================

interface MediaUploadFieldProps {
  type: 'image' | 'video';
  value: string;
  onChange: (value: unknown) => void;
}

function MediaUploadField({ type, value, onChange }: MediaUploadFieldProps) {
  const fileInputRef = useRef<HTMLInputElement>(null);
  const { isUploading, progress, error, uploadFile, clearError } = useMediaUpload();

  const handleFileSelect = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    clearError();
    const result = await uploadFile(file);

    if (result) {
      onChange(result.url);
    }

    // Reset file input
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const handleClear = () => {
    onChange('');
    clearError();
  };

  const acceptTypes = type === 'image'
    ? 'image/jpeg,image/png,image/gif,image/webp'
    : 'video/mp4,video/webm,video/quicktime';

  return (
    <div className="space-y-2">
      {/* Current value preview */}
      {value && (
        <div className="relative inline-block">
          {type === 'image' ? (
            <img
              src={value}
              alt="Preview"
              className="w-24 h-24 object-cover rounded-lg border border-gray-200"
            />
          ) : (
            <video
              src={value}
              className="w-32 h-24 object-cover rounded-lg border border-gray-200"
              controls={false}
            />
          )}
          <button
            type="button"
            onClick={handleClear}
            className="absolute -top-2 -right-2 p-1 bg-red-500 text-white rounded-full hover:bg-red-600 shadow"
          >
            <XIcon />
          </button>
        </div>
      )}

      {/* Upload area */}
      {!value && (
        <div
          className={`
            relative border-2 border-dashed rounded-lg p-6 text-center
            ${isUploading ? 'border-violet-300 bg-violet-50' : 'border-gray-300 hover:border-violet-400'}
            transition-colors cursor-pointer
          `}
          onClick={() => fileInputRef.current?.click()}
        >
          <input
            ref={fileInputRef}
            type="file"
            accept={acceptTypes}
            onChange={handleFileSelect}
            className="hidden"
          />

          {isUploading ? (
            <div className="space-y-2">
              <div className="w-8 h-8 mx-auto border-2 border-violet-600 border-t-transparent rounded-full animate-spin" />
              <p className="text-sm text-violet-600">Uploading... {progress}%</p>
              <div className="w-32 mx-auto h-1 bg-violet-100 rounded-full overflow-hidden">
                <div
                  className="h-full bg-violet-600 transition-all duration-300"
                  style={{ width: `${progress}%` }}
                />
              </div>
            </div>
          ) : (
            <>
              <div className="text-gray-400 mb-2">
                {type === 'image' ? <PhotoIcon /> : <VideoIcon />}
              </div>
              <p className="text-sm text-gray-600">
                Click to upload {type}
              </p>
              <p className="text-xs text-gray-400 mt-1">
                {type === 'image' ? 'JPEG, PNG, GIF, WebP (max 10MB)' : 'MP4, WebM, MOV (max 100MB)'}
              </p>
            </>
          )}
        </div>
      )}

      {/* Error message */}
      {error && (
        <p className="text-sm text-red-600">{error}</p>
      )}

      {/* URL input fallback */}
      <div className="flex items-center gap-2">
        <span className="text-xs text-gray-400">or paste URL:</span>
        <input
          type="url"
          value={value}
          onChange={(e) => onChange(e.target.value)}
          placeholder="https://..."
          className="flex-1 px-2 py-1 text-xs border border-gray-300 rounded focus:outline-none focus:ring-1 focus:ring-violet-500"
        />
      </div>
    </div>
  );
}
